<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Profile webpage -->
<template>
  <div class="profile page">
    <div class="page-heading">
      <h1>Profile</h1>
    </div>
    <!-- Enable/Disable Outlook -->
    <div class="page-content">
      <section class="enable-outlook page-section">
        <div class="placeholder"></div>
        <h4>Outlook Integration</h4>
        <div v-if="outlookEnabled">
          Outlook Integration is currently enabled.
        </div>
        <div v-else>
          Outlook Integration is currently disabled.
        </div>
        <br>
        <av-button v-if="!syncing" class="small hollow" @click="outlookIntegration">
          <i class="fal fa-envelope"></i> {{ outlookEnabled ? 'Disable' : 'Enable' }} Outlook Integration
        </av-button>
        <av-spinner v-if="syncing">{{ outlookEnabled ? 'Disabling' : 'Enabling' }}</av-spinner>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapGetters, mapState } from 'vuex'
import UserService from '../common/userService'
import User from '../models/user'
import OutlookIntegration from '../models/outlookIntegration'
import State from '../store/state'

export default defineComponent({
  name: 'Profile',

  data () {
    return {
      code: '',
      syncing: false,
      user: null as unknown as User,
      args: null as unknown as OutlookIntegration,
      button: {
        text: 'Enable'
      }
      // outlookEnabled: false
    }
  },

  /**
   * sets whether Outlook is enabled/disabled
   */
  mounted () {
    if (this.outlookUserId === '' || this.outlookUserId === null) {
      this.outlookEnabled = false
      this.button.text = 'Enable'
    } else {
      this.outlookEnabled = true
      this.button.text = 'Disable'
    }

    this.isOutlookSyncing()
  },

  computed: {
    ...mapState(['firstName']),
    ...mapState(['lastName']),
    ...mapState(['userId']),
    ...mapState(['outlookUserId']),
    ...mapState(['outlookSyncing']),
    ...mapGetters(['outlookSyncing']),
    ...mapGetters(['outlookEnabled'])
  },

  watch: {
    button: {
      handler () {
        if (this.outlookUserId !== null) {
          this.outlookEnabled = true
        } else {
          this.outlookEnabled = false
        }
      }
    }
  },

  methods: {
    isOutlookSyncing () {
      UserService.isOutlookSyncing(this.userId)
        .then(res => {
          this.syncing = res.data.outlookSyncing
          if (this.syncing) {
            setTimeout(this.isOutlookSyncing, 5000)
          }
        })
    },

    /**
     * Outlook integraion either enabling or disabling determined by previous state
     */
    outlookIntegration () {
      if (this.syncing) { return }
      this.syncing = true

      // Open window to log in
      const windowRef = this.getWindowRef() as Window

      // Start checking for response from AZURE AD with code
      const checkFn = window.setInterval(() => {
        try {
          if (windowRef.document.URL.indexOf(process.env.VUE_APP_AZURE_AD_REDIRECT) !== -1) {
            window.clearInterval(checkFn)
            const url = windowRef.document.URL
            const token = this.parseUrl(url, 'code')
            let isSuccess = false
            this.code = token
            this.args = {
              id: this.userId,
              authCode: this.code
            }

            if (this.outlookEnabled) {
              UserService.disableOutlookIntegration(this.args)
                .then(res => {
                  isSuccess = true
                  const state = {
                    outlookUserId: ''
                  } as State
                  this.$store.dispatch('initializeSession', state)
                  this.syncing = false
                })
                .catch(function (error) {
                  alert('There was a problem disabling Outlook Integration! \n ' + error.message)
                  console.log(error)
                })
                .finally(() => {
                  this.syncing = false
                })

              if (isSuccess) {
                this.outlookEnabled = false
                this.button.text = 'Enable'
              }
            } else {
              UserService.enableOutlookIntegration(this.args)
                .then(res => {
                  isSuccess = false
                  const state = {
                    outlookUserId: res.data.outlookUserId
                  } as State
                  this.$store.dispatch('initializeSession', state)
                  this.syncing = false
                })
                .catch(function (error) {
                  alert('There was a problem enabling Outlook Integration! \n ' + error.message)
                  console.log(error)
                })
                .finally(() => {
                  this.syncing = false
                })

              if (isSuccess) {
                this.outlookEnabled = true
                this.button.text = 'Disable'
              }
            }

            windowRef.close()
          }
        } catch (e) {
          // intentionally blank to prevent spam from XSS warning
        }
      }, 100)
    },

    /**
     * Get window reference
     */
    getWindowRef () {
      return window.open(`https://login.microsoftonline.com/${process.env.VUE_APP_AZURE_AD_TENANT_ID}/oauth2/v2.0/authorize` +
      `?client_id=${process.env.VUE_APP_AZURE_AD_CLIENT_ID}` +
      `&redirect_uri=${process.env.VUE_APP_AZURE_AD_REDIRECT}` +
      `&scope=${process.env.VUE_APP_AZURE_AD_SCOPE}` +
      '&response_type=code' +
      '&response_mode=query' +
      '&state=1234', '_blank', 'width=800, height=600')
    },

    /**
     * Parse URL for code
     */
    parseUrl (url, name): string {
      name = name.replace(/[[]/, '[').replace(/[[]/, ']')
      const regexS = '[?&]' + name + '=([^&#]*)'
      const regex = new RegExp(regexS)
      const results = regex.exec(url)
      if (results == null) {
        return ''
      } else {
        return results[1]
      }
    }
  }
})
</script>
